import React from 'react';
import { MdArrowBack, MdAddCircleOutline, MdFlight } from 'react-icons/md';
import { FaRegCreditCard } from 'react-icons/fa';
import accountsData from '../data/accounts.json';
import { useProfile } from '../contexts/ProfileContext';

// Index of all cards across the customer's profile + the default mock
// data. The CardManagementScreen route holds the per-card detail; this
// screen is the list. Mirrors the bh-account-card pattern from HomeScreen.
const FALLBACK_CARDS = (accountsData.creditDataSummary || []).map(c => ({
  id:       String(c.id ?? c.name),
  name:     c.name,
  number:   c.number || '••••',
  balance:  c.balance,
  limit:    c.creditLimit,
  network:  c.network || 'Visa',
  variant:  'credit',
}));

function getCards(profile) {
  const cards = [];
  if (profile?.accounts?.credit_card) {
    const cc = profile.accounts.credit_card;
    cards.push({
      id: 'credit_card',
      name: cc.card_name || cc.name || 'Credit Card',
      number: '...' + String(cc.account_number || '').slice(-4),
      balance: cc.current_balance ?? cc.current_balance_owing ?? cc.current_value ?? cc.current_market_value ?? cc.balance,
      limit: cc.credit_limit,
      network: cc.network || 'Visa',
      variant: 'credit',
    });
  }
  if (profile?.accounts?.chequing) {
    const ch = profile.accounts.chequing;
    cards.push({
      id: 'debit',
      name: ch.name || 'Debit Card',
      number: '...' + String(ch.account_number || '').slice(-4),
      balance: ch.current_balance ?? ch.current_balance_owing ?? ch.current_value ?? ch.current_market_value ?? ch.balance,
      limit: null,
      network: 'Interac',
      variant: 'debit',
    });
  }
  return cards.length ? cards : FALLBACK_CARDS;
}

const fmtCad = (v) => {
  if (v == null) return '';
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(n)) return '';
  return `$${Math.abs(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const CardsScreen = ({ onNavigate, onBack }) => {
  const { loadedProfileData } = useProfile();
  const cards = getCards(loadedProfileData?.profile);

  return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('accounts'))} aria-label="Back to accounts">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">My Cards</h1>
        </div>
        <FaRegCreditCard size={20} style={{ color: 'var(--bh-black)' }} />
      </header>

      <div className="bh-flow-body">
        {cards.map(c => (
          <button
            key={c.id}
            type="button"
            className="bh-account-card"
            onClick={() => onNavigate('cardMgmt')}
            style={{ gap: 'var(--space-2xs)' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2xs)' }}>
              <p className="bh-account-card-type">{c.network} · {c.variant === 'credit' ? 'Credit' : 'Debit'}</p>
              <FaRegCreditCard size={18} style={{ color: 'var(--bh-gray)' }} />
            </div>
            <p className="bh-account-card-balance" style={{ font: 'var(--bh-h3)' }}>{c.name}</p>
            <p className="bh-account-card-number">{c.number}</p>
            {c.balance != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'var(--space-2xs)' }}>
                <div>
                  <span style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>
                    {c.variant === 'credit' ? 'Balance' : 'Available'}
                  </span>
                  <p style={{ margin: 0, font: 'var(--bh-body)', fontWeight: 700, color: 'var(--bh-black)' }}>
                    {typeof c.balance === 'string' ? c.balance : fmtCad(c.balance)}
                  </p>
                </div>
                {c.limit && (
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>
                      Limit
                    </span>
                    <p style={{ margin: 0, font: 'var(--bh-body)', fontWeight: 700, color: 'var(--bh-black)' }}>
                      {typeof c.limit === 'string' ? c.limit : fmtCad(c.limit)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </button>
        ))}

        <div className="bh-btn-row">
          <button
            type="button"
            className="bh-btn-outline-dark"
            onClick={() => onNavigate('travelNotice')}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2xs)' }}
          >
            <MdFlight /> Travel notice
          </button>
          <button
            type="button"
            className="bh-btn-outline-dark"
            onClick={() => onNavigate('openAccount')}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2xs)' }}
          >
            <MdAddCircleOutline /> Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default CardsScreen;
